import api, { prepareQueryParameters } from "@/store/api";
import { Asset, AssetDetail, AssetTransaction, AssetsGraphData, AssetsGraphDataDual, CompositionDetail, Portfolio, TimeFilter } from "@/types";
import { assetsHistoryToChartDataDual, getDateRange } from "../helpers";

interface AssetDetails {
	[key: number]: AssetDetail;
}

const [from, to] = getDateRange(TimeFilter.MONTHS_3);
const emptyState = () => {
	return {
		loading: true,
		loadingCompositionDetail: true,
		compositionByAssets: [],
		polarisComposition: [],
		polarisValues: [],
		shortHistory: null,
		loadingShortHistory: true,
		loadingList: true,
		filterId: TimeFilter.MONTHS_3,
		list: [] as Asset[],
		from,
		to,
		assetsGraphData: { labels: [], datasets: [] } as AssetsGraphDataDual,
		details: {} as AssetDetails,
		transactions: [] as AssetTransaction[],
		compositionDetail: [] as CompositionDetail[],
		compositionDetailHeading: "",
	}
};

const state = emptyState();
type State = typeof state;

export default {
	namespaced: true,
	state: emptyState(),
	getters: {},
	actions: {
		compositionByAssets(store: any, params: any) {
			store.commit("setLoading", true);
			api
				.get(`assets/composition-by-assets?${prepareQueryParameters(params)}`)
				.then((data) => {
					store.commit("setCompositionByAssets", data.data);
					store.commit("setLoading", false);
				})
				.catch((error) => {
					store.commit("setCompositionByAssets", []);
					store.commit("setLoading", false);
				});
		},
		polarisComposition(store: any) {
			store.commit("setLoading", true);
			api
				.get("assets/polaris-composition")
				.then((data) => {
					store.commit("setPolarisComposition", data.data);
					store.commit("setLoading", false);
				})
				.catch((error) => {
					store.commit("setPolarisComposition", []);
					store.commit("setLoading", false);
				});
		},
		polarisValues(store: any) {
			store.commit("setLoading", true);
			api
				.get("assets/polaris-values")
				.then((data) => {
					store.commit("setPolarisValues", data.data);
					store.commit("setLoading", false);
				})
				.catch((error) => {
					store.commit("setPolarisValues", []);
					store.commit("setLoading", false);
				});
		},
		loadShortHistory(store: any, portfolio?: Portfolio) {
			store.commit("setLoadingShortHistory", true);
			api
				.get(`assets/short-history`)
				.then(({ data }) => {
					store.commit("setShortHistory", data);
					store.commit("setLoadingShortHistory", false);
				})
				.catch((error) => {
					console.log(error);
					store.commit("setLoadingShortHistory", false);
				});
		},
		loadAssetsHistory(store: any, portfolio?: Portfolio) {
			const { from, to } = store.state as State;
			const params = {
				portfolioId: portfolio?.portfolio,
				from: from.toISOString(),
				to: to.toISOString(),
			};
			store.commit("loading", true);
			api
				.get(`assets/history?${prepareQueryParameters(params)}`)
				.then(({ data }) => {
					store.commit("setGraphData", assetsHistoryToChartDataDual(data));
					store.commit("loading", false);
				})
				.catch((error) => {
					console.log(error);
					store.commit("loading", false);
				});
		},
		loadList(store: any, portfolio?: Portfolio) {
			const params = {
				portfolioId: portfolio?.portfolio,
			};
			store.commit("loadingList", true);
			api
				.get(`assets/list?${prepareQueryParameters(params)}`)
				.then(({ data }) => {
					store.commit("setList", data);
					store.commit("resetDetail");
					store.commit("loadingList", false);
				})
				.catch((error) => {
					store.commit("loadingList", false);
				});
		},
		assetDetail(store: any, { code, index }: { code: string; index: number }) {
			if (!store.state.details.hasOwnProperty(code)) {
				const [from, to] = getDateRange(TimeFilter.YEAR);
				const params = {
					from: from.toISOString(),
					to: to.toISOString(),
				};
				api
					.get(`assets/asset/${encodeURIComponent(code)}?${prepareQueryParameters(params)}`)
					.then((data) => {
						store.commit("addDetail", { code, detail: data.data, index });
					})
					.catch((error) => {
						console.log(error);
					});
			}
		},
		assetTransactions(store: any, { code, index, portfolio }: { code: string; index: number, portfolio: string }) {
			if (!store.state.details.hasOwnProperty(code)) {
				const [from, to] = getDateRange(TimeFilter.YEAR);
				const params = {
					from: from.toISOString(),
					to: to.toISOString(),
				};
				api
					.get(`assets/transactions/${encodeURIComponent(code)}?${prepareQueryParameters(params)}`)
					.then((data) => {
						store.commit("addTransactions", { code, transactions: data.data, index });
					})
					.catch((error) => {
						console.log(error);
					});
			}
		},
		assetCompositionDetail(
			store: any,
			{ assetType, assetTypeName, portfolio }: { assetType: string; assetTypeName: string; portfolio?: Portfolio }
		) {
			store.commit("setLoadingComposition", true);
			const params = {
				assetType,
				portfolioId: portfolio?.portfolio,
			};
			api
				.get(`assets/composition-detail?${prepareQueryParameters(params)}`)
				.then((data) => {
					store.commit("addCompositionDetail", { detail: data.data, assetTypeName });
					store.commit("setLoadingComposition", false);
				})
				.catch((error) => {
					console.log(error);
				});
		},
	},
	mutations: {
		setCompositionByAssets(state: State, data: any) {
			state.compositionByAssets = data;
		},
		setPolarisComposition(state: State, data: any) {
			state.polarisComposition = data;
		},
		setPolarisValues(state: State, data: any) {
			state.polarisValues = data;
		},
		setShortHistory(state: State, data: any) {
			state.shortHistory = data;
		},
		setLoading(state: State, data: boolean) {
			state.loading = data;
		},
		setLoadingComposition(state: State, data: boolean) {
			state.loadingCompositionDetail = data;
		},
		setLoadingShortHistory(state: State, loading: boolean) {
			state.loadingShortHistory = loading;
		},
		selectFilter(state: State, filterId: TimeFilter) {
			state.filterId = filterId;
			if (filterId !== TimeFilter.CUSTOM) {
				const [from, to] = getDateRange(filterId);
				state.from = from;
				state.to = to;
			}
		},
		setGraphData(state: State, data: AssetsGraphDataDual) {
			state.assetsGraphData = data;
		},
		loading(state: State, loading: boolean) {
			state.loading = loading;
		},
		loadingList(state: State, loading: boolean) {
			state.loadingList = loading;
		},
		setCustomFilter(state: State, { from, to }: { from: Date; to: Date }) {
			state.from = from;
			state.to = to;
		},
		setList(state: State, list: Asset[]) {
			state.list = list;
		},
		resetDetail(state: State) {
			state.details = {};
		},
		addDetail(state: State, { code, detail, index }: { code: string; detail: AssetDetail; index: number }) {
			state.details = { ...state.details, [code]: detail };
			const assets = state.list;
			assets[index].detail = detail;
			state.list = [...assets];
		},
		addTransactions(state: State, { code, transactions, index }: { code: string; transactions: AssetTransaction[]; index: number }) {
			state.transactions = { ...state.transactions, [code]: transactions };
			const assets = state.list;
			assets[index].transactions = transactions;
			state.list = [...assets];
		},
		addCompositionDetail(
			state: State,
			{ detail, assetTypeName }: { detail: CompositionDetail[]; assetTypeName: string }
		) {
			state.compositionDetail = detail;
			state.compositionDetailHeading = assetTypeName;
		},
		reset (state: any) {
			Object.assign(state, emptyState());
		}
	},
};
